import React from 'react';
import { Autocomplete, TextField, styled } from '@mui/material';
import type { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Colors } from 'library';

type False = false;

interface AutoCompleteProps<T, M extends boolean = False>
    extends Omit<
        AutocompleteProps<T, M, False, False>,
        | 'value'
        | 'onChange'
        | 'options'
        | 'multiple'
        | 'renderInput'
        | 'renderOption'
        | 'getOptionLabel'
        | 'isOptionEqualToValue'
        | 'getOptionDisabled'
        | 'onSelect'
    > {
    multiple: M;
    options: T[];
    selected: M extends true ? T[] : T | null;
    onSelect: (value: M extends true ? T[] : T | null) => void;
    placeholder?: string;
    getOptionLabel: (option: T) => string;
    getOptionSelected?: (option: T, value: T) => boolean;
    getOptionDisabled?: (option: T) => boolean;
    renderOption?: (option: T) => React.ReactNode;
}

export function AutoComplete<T, M extends boolean = False>({
    multiple,
    options,
    selected,
    onSelect,
    placeholder = 'Click here to search',
    getOptionLabel,
    getOptionSelected,
    getOptionDisabled,
    renderOption,
    ...rest
}: AutoCompleteProps<T, M>) {
    const renderInput = (params: AutocompleteRenderInputParams) => (
        <StyledTextField {...params} placeholder={placeholder} />
    );

    return (
        <Autocomplete<T, M, False, False>
            multiple={multiple}
            value={selected}
            options={options}
            onChange={(_, value) => onSelect(value)}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={getOptionSelected}
            getOptionDisabled={getOptionDisabled}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
                const content = renderOption ? renderOption(option) : getOptionLabel(option);
                return (
                    <li key={key} {...optionProps}>
                        {content}
                    </li>
                );
            }}
            renderInput={renderInput}
            {...rest}
        />
    );
}

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        backgroundColor: Colors.surface,
        borderRadius: 10,
        fontSize: 14,
        fontFamily: 'inherit',
        color: Colors.primary,
        '& fieldset': { borderColor: Colors.border },
        '&:hover fieldset': { borderColor: Colors.textSecondary },
        '&.Mui-focused fieldset': { borderColor: Colors.textSecondary, borderWidth: 1 },
    },
});
